import { useNavigationState } from "@/zustand/NavigationState";
import { useLinks } from "@/components/chat/right-column/tabs/links/useLinks";
import LinkContainer from "@/components/chat/right-column/tabs/links/LinkContainer";
import LinkAnchor from "@/components/chat/right-column/tabs/links/LinkAnchor";
import LinkLoading from "@/components/chat/right-column/tabs/links/LinkLoading";
import LinkError from "@/components/chat/right-column/tabs/links/LinkError";
import LinkEmpty from "@/components/chat/right-column/tabs/links/LinkEmpty";
import Link from "@/components/chat/right-column/tabs/links/Link";

/**
 * Link list component
 * Displays a list of links with infinite scrolling
 * Uses custom hook for pagination, loading, and error states
 *
 * How it works:
 * 1. Uses the useLinks hook to manage link data and UI states
 * 2. Handles different view states: loading, error, empty, and content
 * 3. Displays links in chronological order with newest at the top
 * 4. Implements infinite scrolling with intersection observer
 * 5. Shows loading indicators and error states as needed
 *
 * Key features:
 * - Chronological display (newest at top)
 * - Flat list with consistent styling
 * - Infinite scrolling for pagination
 * - Comprehensive error and empty states
 * - Consistent layout with media and files tabs
 *
 * Context:
 * This component is the main container for the links tab in the right column.
 * It follows the same pattern as MediaAttachmentList and FileAttachmentList
 * with newest content at the top.
 */
export default function LinkList() {
  const activeChatId = useNavigationState((state) => state.activeChatId);

  // Use the custom hook for links
  const {
    isLoading,
    error,
    linkAttachments,
    isFullLinkListLoaded,
    scrollerRef,
    linksListRef,
    anchorRef,
    handleRetry,
  } = useLinks(activeChatId || null);

  if (!activeChatId) {
    return null;
  }

  // Show loading state
  if (isLoading && !linkAttachments.length) {
    return <LinkLoading />;
  }

  // Show error state
  if (error) {
    return <LinkError onRetry={handleRetry} />;
  }

  // Show empty state
  if (!isLoading && !linkAttachments.length) {
    return <LinkEmpty />;
  }

  return (
    <LinkContainer scrollerRef={scrollerRef} linksListRef={linksListRef}>
      {/* Error state */}
      {error && <LinkError onRetry={handleRetry} />}

      {/* Display links in a flat list with newest at the top */}
      {
        <div className="flex w-full flex-col gap-2">
          {linkAttachments.map((link) => (
            <Link key={link.id} link={link} />
          ))}
        </div>
      }

      {/* Empty state - only show when not loading and no error */}
      {!isLoading && !error && linkAttachments.length === 0 && <LinkEmpty />}

      {isLoading && <LinkLoading />}
      {/* Top anchor for loading older links */}
      <LinkAnchor
        anchorRef={anchorRef}
        isFullListLoaded={isFullLinkListLoaded}
        hasAttachments={linkAttachments.length > 0}
      />
    </LinkContainer>
  );
}
